import React, { useContext, useEffect, useState } from 'react';
import { getUser, userLogin } from '~/services/auth';
import { IPerson, IUser } from '~/types/campaigns';

// Mirrors api/new_admin/utils.py:get_group — the backend priority used by
// handle_login_response to pick the singular `role`. Used here when
// re-hydrating from a stored token, since /auth/user does not include
// `role` the way the login response does. Without this, the Sidebar's
// role-based filtering (e.g. `role !== 'medical professional'`) shows the
// outreach group on every page refresh.
const ROLE_PRIORITY = [
    'patient services coordinator',
    'medical professional',
    'Financial',
    'administrator',
];

const deriveRole = (roles: string[] | undefined): string | null => {
    if (!roles?.length) return null;
    for (const candidate of ROLE_PRIORITY) {
        if (roles.includes(candidate)) return candidate;
    }
    return null;
};

type InitialAuthType = {
    user: null | IUser;
    signIn: (_username: string, _password: string) => Promise<any>;
    signOut: () => void;
    refreshUser: () => Promise<void>;
    expiry: any;
    person: IPerson | null;
    role: string | null;
};

export const AuthContext = React.createContext<InitialAuthType>({
    user: null,
    signIn: async (_username: string, _password: string) => { },
    signOut: () => { },
    refreshUser: async () => { },
    expiry: null,
    person: null,
    role: null,
});

export const useAuth = () => {
    return useContext(AuthContext);
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState(null);
    const [expiry, setExpiry] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [person, setPerson] = useState<IPerson | null>(null);
    const [role, setRole] = useState<string | null>(null);

    useEffect(() => {
        const initializeAuth = async () => {
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');
            const storedExpiry = localStorage.getItem('expiry');

            // No token means not logged in - finish loading immediately
            if (!storedToken) {
                setLoading(false);
                return;
            }

            // Have a token - need to verify it with the API before proceeding
            try {
                const res = await getUser();

                if (res?.user) {
                    const userWithRoles = {
                        ...res.user,
                        roles: res.person?.roles || [],
                        person: res.person
                    };
                    setUser(userWithRoles);
                    localStorage.setItem('user', JSON.stringify(userWithRoles));
                }
                if (res?.person) {
                    setPerson(res.person);
                }
                setRole(deriveRole(res?.person?.roles));
                if (storedExpiry) {
                    setExpiry(storedExpiry);
                }

                setLoading(false);
            } catch (err: any) {
                console.error('Error verifying token:', err);
                
                // Token is invalid - clear everything
                console.log('Token verification failed, clearing auth data');
                localStorage.clear();
                setUser(null);
                setPerson(null);
                setRole(null);
                setExpiry(null);
                
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const signIn = async (username: string, password: string) => {
        const resp = await userLogin(username, password);
        if (!resp.user) {
            // addToast('Incorrect Credentials', { appearance: 'error' });
            console.log('error');
            return false;
        } else {
            console.log('signed in, setting user');
            // Create enhanced user object that includes roles from the response
            const userWithRoles = {
                ...resp.user,
                roles: resp.roles, // Add roles from the response to the user object
                person: resp.person // Add person data to the user object
            };

            setUser(userWithRoles);
            setPerson(resp.person);
            setRole(resp.role);
            localStorage.setItem('user', JSON.stringify(userWithRoles)); // Store enhanced user object
            localStorage.setItem('token', resp.token);
            localStorage.setItem('expiry', resp.expiry);
            // Store TTL for session expiry tracking
            if (resp.expiry) {
                const ttl = new Date(resp.expiry).getTime() - Date.now();
                localStorage.setItem('sessionTTL', ttl.toString());
            }
            setExpiry(resp.expiry);
            return resp;
        }
    };

    const signOut = async () => {
        console.log('signing out');
        localStorage.clear();
        setUser(null);
        setPerson(null);
        setRole(null);
    };

    const refreshUser = async () => {
        const storedToken = localStorage.getItem('token');

        if (!storedToken) {
            console.log('No token found, cannot refresh user');
            return;
        }

        try {
            const res = await getUser();

            if (res?.user) {
                const userWithRoles = {
                    ...res.user,
                    roles: res.person?.roles || [], // Use roles from person object (PersonLoginSerializer)
                    person: res.person
                };

                setUser(userWithRoles);
                localStorage.setItem('user', JSON.stringify(userWithRoles));
            }
            if (res?.person) {
                setPerson(res.person);
            }
            setRole(deriveRole(res?.person?.roles));

            console.log('User refreshed successfully');
        } catch (err: any) {
            console.error('Error refreshing user:', err);
            
            // If unauthorized, clear everything and force re-login
            if (err?.message === 'Unauthorized') {
                console.log('Token is invalid during refresh, clearing auth data');
                localStorage.clear();
                setUser(null);
                setPerson(null);
                setRole(null);
                setExpiry(null);
            }
        }
    };

    // if (expiry && new Date(expiry) < new Date()) {
    //   console.log('should redirect to login');
    //   localStorage.clear();
    //   return redirect('/login');
    // }

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider
            value={{ user, signIn, signOut, refreshUser, expiry, person, role }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
